import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import './App.css';

// Always loaded components (critical for initial render)
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Lazy loaded components (loaded on demand for better performance)
const Teams = lazy(() => import('./components/Teams'));
const Players = lazy(() => import('./components/Players'));
const Users = lazy(() => import('./components/Users'));
const Profile = lazy(() => import('./components/Profile'));
const TeamDetail = lazy(() => import('./components/TeamDetail'));
const PlayerDetail = lazy(() => import('./components/PlayerDetail'));
const TrainingAttendance = lazy(() => import('./components/TrainingAttendance'));
const TrainingStatistics = lazy(() => import('./components/TrainingStatistics'));
const Database = lazy(() => import('./components/Database'));
const Logs = lazy(() => import('./components/Logs'));
const PitchAllocation = null; // Removed - Platzeinteilung entfernt
const GameSchedule = lazy(() => import('./components/GameSchedule'));
const Sponsors = lazy(() => import('./components/Sponsors'));
const Hundertertafel = lazy(() => import('./components/Hundertertafel'));
const YouthBudget = lazy(() => import('./components/YouthBudget'));
const PublicPitchDisplay = null; // Removed - öffentliche Anzeige entfernt
const ChangingRooms = lazy(() => import('./components/ChangingRooms'));
const GoalkeeperTraining = lazy(() => import('./components/GoalkeeperTraining'));
const GoalkeeperStatistics = lazy(() => import('./components/GoalkeeperStatistics'));
const PublicChangingRoomsDisplay = lazy(() => import('./components/PublicChangingRoomsDisplay'));
const MeetingPoint = lazy(() => import('./components/MeetingPoint'));
const ProductInfo = lazy(() => import('./components/ProductInfo'));
const Matchlogs = lazy(() => import('./components/Matchlogs'));
const TacticalBoardPublic = lazy(() => import('./components/TacticalBoardPublic'));
const TrainingExercises = lazy(() => import('./components/TrainingExercises'));
const PlayerEvaluation = lazy(() => import('./components/PlayerEvaluation'));
const Wiki = lazy(() => import('./components/Wiki'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
  </div>
);

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = 'SK Jenbach App';
  }, []);

  // Set up axios interceptor for auth
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuth();
    } else {
      setLoading(false);
    }

    // Setup axios response interceptor for automatic logout on 401
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - logout user
          console.log('Token expired - logging out');
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Redirect Platzwart to Spiel/Platz Termine if on Dashboard
      if (response.data.role === 'platzwart' && window.location.pathname === '/') {
        window.location.href = '/game-schedule';
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim().toLowerCase(),
        password: password.trim()
      });
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      
      // Store token expiry time (2 hours from now)
      const expiryTime = new Date().getTime() + (120 * 60 * 1000); // 120 minutes
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Redirect Platzwart to Spiel/Platz Termine
      if (userData.role === 'platzwart') {
        window.location.href = '/game-schedule';
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Anmeldung fehlgeschlagen' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      
      // Store expiry time (2 hours from now)
      const expiryTime = new Date().getTime() + (120 * 60 * 1000); // 120 minutes
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  // Setup token refresh timer - refresh 5 minutes before expiry
  useEffect(() => {
    if (!user) return;

    const checkAndRefreshToken = () => {
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      if (!tokenExpiry) {
        // No expiry stored, set it now (2 hours from now)
        const expiryTime = new Date().getTime() + (120 * 60 * 1000);
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        return;
      }

      const timeUntilExpiry = parseInt(tokenExpiry) - new Date().getTime();
      const fiveMinutes = 5 * 60 * 1000;

      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
        console.log('Token expiring soon - refreshing...');
        refreshToken();
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Check every minute
    const interval = setInterval(checkAndRefreshToken, 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const authValue = {
    user,
    login,
    logout,
    loading,
    refreshToken
  };

  return (
    <AuthContext.Provider value={authValue}>
      <Router>
        <div className="App min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
          <Toaster position="top-right" richColors />
          
          <Routes>
            <Route 
              path="/login" 
              element={user ? <Navigate to="/" /> : <Login />} 
            />
            
            <Route 
              path="/public/changing-rooms-display" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PublicChangingRoomsDisplay />
                </Suspense>
              } 
            />

            {/* Public Tactical Board - No login required */}
            <Route 
              path="/taktiktafel" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <TacticalBoardPublic />
                </Suspense>
              } 
            />

            {/* Public Product Info Page - No login required */}
            <Route 
              path="/info" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProductInfo />
                </Suspense>
              } 
            />
            
            <Route 
              path="/*" 
              element={
                <ProtectedRoute>
                  <div className="flex h-screen overflow-hidden">
                    <Sidebar 
                      sidebarOpen={sidebarOpen} 
                      setSidebarOpen={setSidebarOpen} 
                    />
                    
                    <div className="flex flex-col flex-1 overflow-hidden">
                      <Navbar 
                        setSidebarOpen={setSidebarOpen}
                        sidebarOpen={sidebarOpen}
                      />
                      
                      <main className="flex-1 overflow-y-auto p-6">
                        <Suspense fallback={<LoadingFallback />}>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/teams" element={<Teams />} />
                            <Route path="/teams/:teamId" element={<TeamDetail />} />
                            <Route path="/teams/:teamId/training" element={<TrainingAttendance />} />
                            <Route path="/players" element={<Players />} />
                            <Route path="/players/:playerId" element={<PlayerDetail />} />
                            <Route path="/training-statistics" element={<TrainingStatistics />} />
                            <Route path="/game-schedule" element={<GameSchedule />} />
                            <Route path="/changing-rooms" element={<ChangingRooms />} />
                            <Route path="/meeting-point" element={<MeetingPoint />} />
                            <Route path="/goalkeeper-training" element={<GoalkeeperTraining />} />
                            <Route path="/goalkeeper-statistics" element={<GoalkeeperStatistics />} />
                            <Route path="/database" element={<Database />} />
                            <Route path="/logs" element={<Logs />} />
                            <Route path="/sponsors" element={<Sponsors />} />
                            <Route path="/hundertertafel" element={<Hundertertafel />} />
                            <Route path="/youth-budget" element={<YouthBudget />} />
                            <Route path="/users" element={<Users />} />
                            <Route path="/matchlogs" element={<Matchlogs />} />
                            <Route path="/training-exercises" element={<TrainingExercises />} />
                            <Route path="/player-evaluation" element={<PlayerEvaluation />} />
                            <Route path="/wiki" element={<Wiki />} />
                          </Routes>
                        </Suspense>
                      </main>
                    </div>
                  </div>
                </ProtectedRoute>
              } 
            />
          </Routes>
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;